import { z } from 'zod'

// Live chat staff inbox (#284 B3): the website chat's conversations as the CMS shows them. The chats live in the
// landing Worker's Durable Objects; the CMS API reads them over its CHAT service binding and answers these shapes.
// Only a CMS whose API has that binding (digimasi.com's) has the feature: anywhere else GET /livechat/status says
// `enabled: false` and the menu item stays hidden.

export const LIVECHAT_LIMITS = {
  /** A staff reply or internal note, in characters. */
  text: 1000,
  /** How long a chat is kept after its last message, in days (the landing Worker deletes it). */
  retentionDays: 7,
} as const

export const LIVECHAT_LANGS = ['id', 'en', 'zh', 'vi', 'ja'] as const

export const LiveChatSummarySchema = z.object({
  id: z.string(),
  status: z.enum(['open', 'closed']),
  lang: z.string(),
  page: z.string(),
  createdAt: z.string(),
  lastMessageAt: z.string().nullable(),
  /** The visitor's last message, at most 80 characters. */
  preview: z.string(),
  /** DigiCS handed the chat over to a person. */
  needsPerson: z.boolean(),
  /** The visitor left a name and email in the hand-over form. */
  emailLeft: z.boolean(),
  /** The staff member who took the chat; while someone holds it the bot stays silent. */
  holderId: z.string().nullable(),
  holderName: z.string().nullable(),
})
export type LiveChatSummary = z.infer<typeof LiveChatSummarySchema>

/** The list's tabs: "Needs a person" (handed over, open, nobody took it yet), the other open chats, closed chats. */
export type LiveChatTab = 'needs' | 'open' | 'closed'
export const liveChatTab = (c: Pick<LiveChatSummary, 'status' | 'needsPerson' | 'holderId'>): LiveChatTab =>
  c.status === 'closed' ? 'closed' : c.needsPerson && !c.holderId ? 'needs' : 'open'

export const LiveChatListSchema = z.object({
  /** LIVE_CHAT on the website: when false, visitors can't start a chat (the inbox still shows the last 7 days). */
  siteOn: z.boolean(),
  items: z.array(LiveChatSummarySchema),
  counts: z.object({ needs: z.number().int(), open: z.number().int(), closed: z.number().int() }),
})
export type LiveChatList = z.infer<typeof LiveChatListSchema>

export const LiveChatStatusSchema = z.object({ enabled: z.boolean(), siteOn: z.boolean(), needsPerson: z.number().int() })
export type LiveChatStatus = z.infer<typeof LiveChatStatusSchema>

export const LiveChatEventSchema = z.object({
  seq: z.number().int(),
  id: z.string(),
  role: z.enum(['visitor', 'bot', 'staff', 'system']),
  /** For staff events the CMS user id: who said or did what. */
  authorId: z.string(),
  /** That user's name in this CMS (staff events only; empty when the user is gone). */
  authorName: z.string().optional(),
  /** message, notice, contact_form, contact, suggestion, note, staff_action. */
  kind: z.string(),
  text: z.string().nullable(),
  /** internal: the visitor never sees it (suggestions, the contact form's answers, notes, staff actions). */
  visibility: z.enum(['public', 'internal']),
  /** The prepared answer's code (bot messages and suggestions). */
  ref: z.string().nullable(),
  createdAt: z.string(),
  contact: z.object({ name: z.string(), email: z.string(), consent: z.boolean() }).nullable().optional(),
  action: z
    .object({ type: z.string(), name: z.string().optional(), to: z.string().optional(), from: z.string().optional(), code: z.string().optional() })
    .nullable()
    .optional(),
  /** A suggestion that staff already sent to the visitor. */
  sent: z.boolean().optional(),
})
export type LiveChatEvent = z.infer<typeof LiveChatEventSchema>

export const LiveChatDetailSchema = z.object({ conversation: LiveChatSummarySchema, events: z.array(LiveChatEventSchema) })
export type LiveChatDetail = z.infer<typeof LiveChatDetailSchema>

/** The answer to a staff action: the chat's new state and the event it added (none for a take by the holder). */
export const LiveChatActionResultSchema = z.object({ conversation: LiveChatSummarySchema, event: LiveChatEventSchema.nullable() })
export type LiveChatActionResult = z.infer<typeof LiveChatActionResultSchema>

/** Colleagues a chat can be handed to: active users who may answer chats (livechat:edit). */
export const LiveChatStaffListSchema = z.object({ items: z.array(z.object({ id: z.string(), name: z.string() })) })
export type LiveChatStaffList = z.infer<typeof LiveChatStaffListSchema>

const staffText = z
  .string()
  .trim()
  .min(1, 'Write a message first')
  .refine((v) => [...v].length <= LIVECHAT_LIMITS.text, `At most ${LIVECHAT_LIMITS.text} characters`)

export const LiveChatReplyRequestSchema = z.object({
  text: staffText,
  /** Chosen by the CMS page; a retry with the same id is stored once. */
  clientMsgId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
})
export const LiveChatNoteRequestSchema = z.object({ text: staffText })
/** `force` (admins only) takes the chat from the colleague who holds it. */
export const LiveChatTakeRequestSchema = z.object({ force: z.boolean().default(false) })
export const LiveChatHandoffRequestSchema = z.object({ userId: z.string().min(1).max(64) })
export const LiveChatSuggestionRequestSchema = z.object({ seq: z.number().int().min(1) })
